import React, { useState, useEffect, useRef, useContext, lazy, Suspense } from 'react';
import { LanguageContext } from './context/LanguageContext';
import { isStaffMode } from './services/staffMode'; // 👷 Mitarbeiter-Modus
import { Routes, Route, Link, Navigate, useLocation, useNavigate } from 'react-router-dom';
import './AppLayout.css';
import AddPackageWithAI from './AddPackageWithAI';
import TrackingForm from './TrackingForm';
import { isMetaQuest } from './utils/deviceDetection';
import AvvGate from './components/AvvGate';
// 🚀 PERFORMANCE: Lazy Loading für selten genutzte Routen
const Statistics = lazy(() => import('./Statistics'));
const TabletHome = lazy(() => import('./bench/TabletHome'));
const Chronik = lazy(() => import('./Chronik'));
const QuestInputLayout = lazy(() => import('./components/quest/QuestInputLayout'));
const VRPackagesVanilla = lazy(() => import('./pages/VRPackagesVanilla'));
const VRPackagesQR = lazy(() => import('./pages/VRPackagesQR'));
const VRSetupMode = lazy(() => import('./pages/VRSetupMode'));
const VRMenu = lazy(() => import('./pages/VRMenu'));
const RoomSelection = lazy(() => import('./pages/RoomSelection'));
const RoomAdmin = lazy(() => import('./pages/RoomAdmin'));
const RoomBuilder = lazy(() => import('./pages/RoomBuilder'));
const RegalQRGenerator = lazy(() => import('./pages/RegalQRGenerator'));
const DocumentManagement = lazy(() => import('./DocumentManagementV2'));
const AdminDashboard = lazy(() => import('./pages/AdminDashboard'));
const Tutorial = lazy(() => import('./pages/Tutorial/Tutorial'));
const CustomerNotificationPage = lazy(() => import('./pages/CustomerNotificationPage'));
import { get, set } from 'idb-keyval';
import { useTranslation } from './hooks/useTranslation';

export const texts = {
  de: {
    name: "Name",
    street: "Straße und Hausnummer",
    city: "PLZ und Ort",
    tracking: "Sendungsnummer",
    shelf: "Regalplatz",
    stored: "Eingelagert am",
    issued: "Ausgegeben am",
    returned: "Zurückgeschickt",
    save: "Speichern",
    cancel: "Abbrechen",
    search: "Suchen",
    noResults: "Keine Ergebnisse gefunden",
    export: "Exportieren",
    import: "Importieren",
    deleted: "Paket gelöscht.",
    undo: "Rückgängig",
    statisticsTitle: "Statistik",
    packages: "Pakete",
    trackingTitle: "Sendungsverfolgung",
    freeLimit: "Kostenlos: {used} von {limit} Paketen genutzt ({free} frei)",
    freeLimitUsed: "Kostenloses Kontingent aufgebraucht ({limit} von {limit})",
    totalIssued: "Ausgegeben (gesamt)",
    totalReturned: "Zurückgeschickt (gesamt)",
    totalDeleted: "Gelöscht (gesamt)",
    avgProcessingTime: "Ø Erfassungsdauer",
    avgIssueTime: "Ø Ausgabedauer",
    totalPackagesCurrent: "Gesamtpakete (aktuell)",
    currentlyInStock: "Aktuell lagernd",
    issuedCurrently: "Ausgegeben (aktuell)",
    returnedCurrently: "Zurückgeschickt (aktuell)",
    packagesPerDayLast30Days: "Pakete pro Tag (letzte 30 Tage):",
    currentlyReturned: "Zurückgeschickt (aktuell)",
    currentlyDeleted: "Gelöscht (aktuell)",
    packagesPerMonthLast12Months: "Pakete pro Monat (letzte 12 Monate):",
    packagesPerYearLast5Years: "Pakete pro Jahr (letzte 5 Jahre):",
    last5ProcessingDurations: "Letzte 5 Erfassungsdauern (Sekunden):",
    resetStatistics: "Statistik zurücksetzen",
    issue: "Ausgeben",
    return: "Zurückschicken",
    delete: "Löschen",
    edit: "Bearbeiten",
    details: "Details",
    tabletSearch: "Tablet-Suche",
    chronik: "Chronik",
    settings: "Einstellungen"
  },
  en: {
    name: "Name",
    street: "Street and Number",
    city: "ZIP and City",
    tracking: "Tracking Number",
    shelf: "Shelf",
    stored: "Stored on",
    issued: "Picked up on",
    returned: "Returned",
    save: "Save",
    cancel: "Cancel",
    search: "Search",
    noResults: "No results found",
    export: "Export",
    import: "Import",
    deleted: "Package deleted.",
    undo: "Undo",
    statisticsTitle: "Statistics",
    packages: "Packages",
    trackingTitle: "Tracking",
    freeLimit: "Free: {used} of {limit} packages used ({free} left)",
    freeLimitUsed: "Free quota used up ({limit} of {limit})",
    totalIssued: "Total picked up",
    totalReturned: "Total returned",
    totalDeleted: "Total deleted",
    avgProcessingTime: "Avg. processing time",
    avgIssueTime: "Avg. pick up time",
    totalPackagesCurrent: "Total packages (current)",
    currentlyInStock: "Currently in stock",
    issuedCurrently: "picked up (current)",
    returnedCurrently: "Returned (current)",
    packagesPerDayLast30Days: "Packages per day (last 30 days):",
    packagesPerDayLast7Days: "Packages per day (last 7 days):",
    currentlyReturned: "Returned (current)",
    currentlyDeleted: "Deleted (current)",
    packagesPerMonthLast12Months: "Packages per month (last 12 months):",
    packagesPerYearLast5Years: "Packages per year (last 5 years):",
    last5ProcessingDurations: "Last 5 processing durations (seconds):",
    resetStatistics: "Reset statistics",
    issue: "Pick up",
    return: "Return",
    delete: "Delete",
    edit: "Edit",
    details: "Details",
    tabletSearch: "Tablet Search",
    chronik: "History",
    documentManagement: "Document Management", 
    settings: "Settings"
  }
};

function App() {
  const { t } = useTranslation(); // untere Navigation in der App-Sprache
  const location = useLocation(); // 🔄 Für Statistik-Refresh bei Navigation
  const navigate = useNavigate();
  const [pakete, setPakete] = useState([]);
  const [darkMode, setDarkMode] = useState(() => {
    try { return localStorage.getItem('kiosk_dark_mode') === '1'; } catch { return false; }
  });
  const [lang, setLang] = useState('de');
  // 🖥️ Untere Navigation im Bench-Vollbild ausblenden (Event aus BenchMode)
  const [hideBottomNav, setHideBottomNav] = useState(false);
  const [navHiddenByKeyboard, setNavHiddenByKeyboard] = useState(false); // Suche fokussiert → Nav weg (nicht über Tastatur schweben)
  const [navHiddenByStaff, setNavHiddenByStaff] = useState(() => isStaffMode() && !!localStorage.getItem('kiosk_jwt_access')); // 👷 Mitarbeiter-Modus: keine Navigation
  useEffect(() => {
    // Nur mit Anmeldung (Access-Token vorhanden) — ausgeloggt immer normale Navigation
    const sync = () => setNavHiddenByStaff(isStaffMode() && !!localStorage.getItem('kiosk_jwt_access'));
    window.addEventListener('kiosk:staff-mode', sync);
    window.addEventListener('kiosk:custom-features', sync);
    return () => { window.removeEventListener('kiosk:staff-mode', sync); window.removeEventListener('kiosk:custom-features', sync); };
  }, []);
  const [navHiddenByModal, setNavHiddenByModal] = useState(false); // Erfassen-/Speichern-Modal offen → Nav weg (schwebt sonst über der Tastatur)
  const [isAdmin, setIsAdmin] = useState(false);
  const [update, setUpdate] = useState(null); // {version, installing} wenn neuer Web-Deploy verfügbar
  const updateOpenRef = useRef(false);
  // 🔄 Update-Dialog statt sofortigem Neustart (Wunsch Philipp, 10.10.2026): „Jetzt installieren" oder „Später (5 Min.)".
  // Ohne Bedienung installiert sich das Update nach 10 Minuten von selbst; die unbeaufsichtigte Foto-Station lädt
  // wie bisher sofort neu (dort drückt niemand). Hintergrund: Ein automatischer Neustart mitten im Scannen hat am
  // 08.10. zu Datenverlust beigetragen.
  const updateSnoozeRef = useRef(0);     // bis wann „Später" gilt
  const lastTouchRef = useRef(Date.now()); // letzte Bedienung (für „ohne Bedienung installieren")
  const unbeaufsichtigt = () => { try { const r = localStorage.getItem('kiosk_device_role'); return r === 'kamera' || r === 'camera'; } catch { return false; } };
  const reloadMitCacheBust = () => {
    try {
      const base = window.location.href.split('#')[0].replace(/([?&])v=\d+/, '$1v=' + Date.now());
      const nextUrl = base.indexOf('v=') >= 0 ? base : base + (base.indexOf('?') >= 0 ? '&' : '?') + 'v=' + Date.now();
      window.location.replace(nextUrl);
    } catch {
      try { window.location.reload(); } catch { /* */ }
    }
  };
  const updateInstallieren = () => {
    if (updateOpenRef.current === 'installing') return;
    updateOpenRef.current = 'installing';
    setUpdate((u) => ({ ...(u || {}), installing: true }));
    // Erst ungesyncte Pakete hochladen lassen (bis 8 s), dann frisch mit neuem ?v=-Timestamp laden
    const start = Date.now();
    const warte = async () => {
      try {
        const list = (await get('pakete')) || [];
        const offen = list.some((p) => p && p.uploaded === false);
        if (offen && Date.now() - start < 8000) { setTimeout(warte, 1000); return; }
      } catch { /* */ }
      reloadMitCacheBust();
    };
    warte();
    setTimeout(() => { if (updateOpenRef.current === 'installing') { updateOpenRef.current = false; setUpdate(null); } }, 30000);
  };
  const updateSpaeter = () => { updateSnoozeRef.current = Date.now() + 5 * 60 * 1000; setUpdate(null); updateOpenRef.current = 'snoozed'; };
  useEffect(() => {
    const merke = () => { lastTouchRef.current = Date.now(); };
    window.addEventListener('pointerdown', merke, { passive: true });
    window.addEventListener('keydown', merke, { passive: true });
    return () => { window.removeEventListener('pointerdown', merke); window.removeEventListener('keydown', merke); };
  }, []);
  const colors = darkMode
    ? { darkMode: true, text: '#fff', box: '#23272f', button: '#1976d2', inputBg: '#23272f', border: '#444' }
    : { darkMode: false, text: '#23272f', box: '#f7f7f7', button: '#1976d2', inputBg: '#fff', border: '#ccc' };

  // 🛟 Boot-Wächter (index.html) melden: App ist korrekt gestartet
  useEffect(() => { window.__KIOSK_BOOT_OK = true; }, []);

  // 🌓 Dark Mode: Body-Klasse und Speichern (auch in localStorage für Konto-Profil-Sync)
  useEffect(() => {
    document.body.classList.toggle('darkmode', darkMode);
    document.documentElement.classList.toggle('darkmode', darkMode); // html Element
    set('settings-darkMode', darkMode);
    try { localStorage.setItem('kiosk_dark_mode', darkMode ? '1' : '0'); } catch { /* */ }
  }, [darkMode]);

  // 🌐 Sprache speichern
  useEffect(() => {
    set('settings-lang', lang);
  }, [lang]);

  // 📱 Tablet-Modus merken: War das Gerät zuletzt im Tablet-Modus (/tablet-search),
  // öffnet die App nach einem Neustart direkt wieder dort (Geräte-Rolle darin wird
  // separat über kiosk_device_role gemerkt). Beim ersten Render einmalig umleiten,
  // danach bei jeder Navigation den Zustand fortschreiben.
  const tabletRestoredRef = useRef(false);
  useEffect(() => {
    if (location.pathname.startsWith('/benachrichtigung')) return;
    if (!tabletRestoredRef.current) {
      tabletRestoredRef.current = true;
      try {
        if (localStorage.getItem('kiosk_last_tablet_mode') === '1' && location.pathname !== '/tablet-search') {
          navigate('/tablet-search', { replace: true });
          return;
        }
      } catch { /* */ }
    }
    try { localStorage.setItem('kiosk_last_tablet_mode', location.pathname === '/tablet-search' ? '1' : '0'); } catch { /* */ }
  }, [location.pathname, navigate]);

  // 🔄 Auto-Update (Testphase): alle 60 s prüfen, ob ein neuer Web-Deploy vorliegt
  // (asset-manifest.json ändert sich bei jeder Code-Änderung).
  // WICHTIG: Prüft immer gegen das tatsächlich im DOM geladene main.<hash>.js, damit weder
  // ein zweites Gerät im selben Konto (über station:reload) noch ein gecachter ?v=-Reload
  // fälschlich „Update verfügbar" anzeigt, obwohl das Handy längst aktuell ist.
  const getLoadedMainBundle = () => {
    try {
      const scripts = Array.from(document.querySelectorAll('script[src]'));
      for (const s of scripts) {
        const m = String(s.getAttribute('src') || '').match(/main\.[a-f0-9]+\.js/i);
        if (m) return m[0];
      }
    } catch { /* */ }
    return '';
  };
  const extractManifestMainBundle = (rawText) => {
    try {
      const data = JSON.parse(rawText);
      const mainPath = data?.files?.['main.js'] || '';
      const m = String(mainPath).match(/main\.[a-f0-9]+\.js/i);
      return m ? m[0] : String(rawText || '').trim();
    } catch {
      const m = String(rawText || '').match(/main\.[a-f0-9]+\.js/i);
      return m ? m[0] : String(rawText || '').trim();
    }
  };

  useEffect(() => {
    let running = getLoadedMainBundle();
    let relayedBundle = '';
    let cancelled = false;
    const fetchText = async (url) => {
      const res = await fetch(`${url}?t=${Date.now()}`, { cache: 'no-store' });
      if (!res.ok) throw new Error(url);
      return res.text();
    };
    (async () => {
      if (!running) {
        try { running = extractManifestMainBundle(await fetchText('/asset-manifest.json')); } catch { /* */ }
      }
    })();

    const check = async () => {
      if (cancelled || updateOpenRef.current === 'installing') return; // Update läuft bereits
      if (navigator.onLine === false) return;         // offline → Reload wäre riskant (hängende Fehlerseite)
      // „Später" gedrückt: nach 5 Minuten erneut fragen — oder ohne Bedienung (10 Min.) still installieren
      if (updateOpenRef.current === 'snoozed') {
        const idle = Date.now() - lastTouchRef.current > 10 * 60 * 1000 && !window.__kioskBusy;
        if (Date.now() < updateSnoozeRef.current && !idle) return;
        updateOpenRef.current = false;
        if (idle) { updateInstallieren(); return; }
      }
      if (updateOpenRef.current) return; // Dialog steht schon
      let latestRaw;
      try { latestRaw = await fetchText('/asset-manifest.json'); } catch { return; }
      const latest = extractManifestMainBundle(latestRaw);
      if (!latest) return;
      if (!running) { running = latest; return; }
      const domMain = getLoadedMainBundle();
      if (latest === running || (domMain && latest === domMain)) {
        running = domMain || latest;
        return;
      }
      // ⏳ NICHT mitten in den laufenden Deploy springen: Der Upload löscht erst die alten
      // Dateien und lädt dann ~100 neue hoch. Wer in diesem Fenster neu lädt, bekommt einen
      // halben Bundle → weißer Bildschirm (so ist die Kamera-Station am 09.07. gestorben).
      // Deshalb: 10 s warten und nur neu laden, wenn das Manifest dann UNVERÄNDERT ist
      // (Deploy abgeschlossen). Ändert es sich noch, prüft der nächste 60-s-Tick erneut.
      await new Promise((r) => setTimeout(r, 10000));
      let settledRaw;
      try { settledRaw = await fetchText('/asset-manifest.json'); } catch { return; }
      const settled = extractManifestMainBundle(settledRaw);
      if (settled !== latest) { return; }
      if (domMain && settled === domMain) { running = domMain; return; }
      let version = '';
      let forceUpdate = false;
      try {
        const v = JSON.parse(await fetchText('/version.json'));
        version = v.version || v.appVersion || '';
        forceUpdate = Boolean(v.forceUpdate);
      } catch { /* */ }
      // 🔄 Kamera-Station nur EINMAL pro neuem Bundle anstoßen (nicht alle 5 Min. nach Snooze!)
      if (relayedBundle !== settled) {
        relayedBundle = settled;
        try {
          const { relaySignal } = await import('./services/stationService');
          relaySignal('station:reload', { reason: 'deploy', bundle: settled }).catch(() => { /* */ });
        } catch { /* */ }
      }
      if (unbeaufsichtigt()) { updateInstallieren(); return; } // Station: niemand drückt → sofort
      // Normale Hintergrund-Deploys (forceUpdate === false): KEIN störendes Vollbild-Popup!
      // Stattdessen still im Hintergrund vormerken und erst bei Inaktivität (10 Min. ohne Touch)
      // oder beim nächsten App-Start aktualisieren.
      if (!forceUpdate) {
        updateOpenRef.current = 'snoozed';
        updateSnoozeRef.current = Date.now() + 10 * 60 * 1000;
        return;
      }
      updateOpenRef.current = 'dialog';
      setUpdate({ version, installing: false });
    };
    const id = setInterval(check, 60000);
    return () => { cancelled = true; clearInterval(id); };
  }, []);

  // 🔄 Deploy-Reload-Empfänger: Nur für die unbeaufsichtigte Kamera-Station.
  // Bediente Handys prüfen selbst im Hintergrund und zeigen kein störendes Popup.
  useEffect(() => {
    let close = null; let cancelled = false;
    (async () => {
      try {
        const { connectSSE } = await import('./services/apiService');
        if (cancelled) return;
        close = connectSSE({
          'station:reload': async (payload) => {
            if (!unbeaufsichtigt()) {
              return; // Bediente Handys niemals durch SSE-Broadcast mit einem Update-Popup unterbrechen
            }
            const domMain = getLoadedMainBundle();
            if (payload?.bundle && domMain && payload.bundle === domMain) {
              return;
            }
            const attempt = (n) => {
              if (cancelled) return;
              if (n < 10 && window.__kioskBusy) { setTimeout(() => attempt(n + 1), 30000); return; }
              reloadMitCacheBust();
            };
            setTimeout(() => attempt(0), 2500);
          },
        });
      } catch { /* */ }
    })();
    return () => { cancelled = true; try { if (close) close(); } catch { /* */ } };
  }, []);

  // 📦 Einstellungen beim Start laden
  useEffect(() => {
    get('pakete').then(pakete => setPakete(pakete || []));
    // Gespeicherte Einstellungen laden
    get('settings-darkMode').then(saved => {
      try {
        const ls = localStorage.getItem('kiosk_dark_mode');
        if (ls === '1' || ls === '0') {
          setDarkMode(ls === '1');
          return;
        }
      } catch { /* */ }
      if (saved !== undefined) setDarkMode(Boolean(saved));
    });
    const onProfileAppliedDark = () => {
      try {
        const ls = localStorage.getItem('kiosk_dark_mode');
        if (ls === '1' || ls === '0') setDarkMode(ls === '1');
      } catch { /* */ }
    };
    window.addEventListener('kiosk:profile-applied', onProfileAppliedDark);
    get('settings-lang').then(saved => {
      if (saved !== undefined) setLang(saved);
    });
    
    // 🛡️ Admin-Check: Beim App-Start UND nach Login
    const checkAdmin = () => {
      import('./services/apiService').then(({ apiFetch, isLoggedIn }) => {
        if (isLoggedIn()) {
          apiFetch('/admin/check').then(() => setIsAdmin(true)).catch(() => {});
        }
      });
    };
    checkAdmin();

    // Auch nach Login prüfen (Event wird von useServerAuth gefeuert)
    const handleAuthChange = () => setTimeout(checkAdmin, 500);
    window.addEventListener('kiosk:auth-changed', handleAuthChange);
    // 🖥️ Bench-Vollbild → untere Navigation ausblenden
    const handleBenchFs = (e) => setHideBottomNav(!!e.detail);
    window.addEventListener('kiosk:bench-fullscreen', handleBenchFs);
    // 💾 Erfassen-/Speichern-Modal offen → untere Nav ausblenden (nimmt sonst über der Tastatur Platz weg)
    const handleSaveModal = (e) => setNavHiddenByModal(!!e.detail);
    window.addEventListener('kiosk:save-modal', handleSaveModal);
    // ⌨️ Tastatur offen (visualViewport schrumpft) → untere Nav ausblenden, sonst zeigen.
    // Zuverlässiger als focus/blur: das feuert auf Android beim Schließen der Tastatur
    // oft nicht (dadurch kam die Nav nie zurück). So folgt die Nav exakt der Tastatur.
    const vv = window.visualViewport;
    const onVvResize = () => { if (vv) setNavHiddenByKeyboard((window.innerHeight - vv.height) > 150); };
    if (vv) { vv.addEventListener('resize', onVvResize); onVvResize(); }
    return () => {
      window.removeEventListener('kiosk:profile-applied', onProfileAppliedDark);
      window.removeEventListener('kiosk:auth-changed', handleAuthChange);
      window.removeEventListener('kiosk:bench-fullscreen', handleBenchFs);
      window.removeEventListener('kiosk:save-modal', handleSaveModal);
      if (vv) vv.removeEventListener('resize', onVvResize);
    };
  }, []);

  // 🥽 Quest Content: Nur Paket-Erfassung
  const questContent = (
    <div style={{ width: '100%', height: '100%', padding: '20px', background: '#fff' }}>
      <QuestInputLayout 
        user={null}
        pakete={pakete}
        onPackageSaved={(newPaket) => {
          setPakete(prev => [...prev, newPaket]);
        }}
      />
    </div>
  );

  // 🚀 PERFORMANCE: Loading-Fallback für lazy-geladene Komponenten
  const LoadingFallback = () => (
    <div style={{ 
      display: 'flex', 
      justifyContent: 'center', 
      alignItems: 'center', 
      height: '100vh',
      background: darkMode ? '#1a1a1a' : '#fff',
      color: darkMode ? '#fff' : '#333'
    }}>
      <div style={{ textAlign: 'center' }}>
        <div style={{ fontSize: '24px', marginBottom: '10px' }}>⏳</div>
        <div>{t('ui.app.loading')}</div>
      </div>
    </div>
  );

  return (
    <>
    {/* 📜 AVV-Gate: blockiert nach dem Login, bis der Betreiber den Auftrags-
        verarbeitungsvertrag bestätigt hat. Selbstprüfend, kein Eingriff in die App. */}
    <AvvGate />
    {update && <UpdateDialog update={update} onNow={updateInstallieren} onLater={updateSpaeter} />}
    <Suspense fallback={<LoadingFallback />}>
    <Routes>
      {/* 🔔 Öffentliche Kunden-Benachrichtigung per QR-Code (ohne Shop-Navigation) */}
      <Route path="/benachrichtigung" element={<CustomerNotificationPage />} />

      {/* VR Routes - AUSSERHALB des normalen Layouts (Fullscreen) */}
      <Route path="/vr" element={<VRPackagesVanilla />} />
      <Route path="/vr/packages" element={<VRPackagesVanilla />} />
      <Route path="/vr/packages-qr" element={<VRPackagesQR />} /> {/* NEU: QR-Gaze basierte Paketerfassung */}
      <Route path="/vr/qr-generator" element={<RegalQRGenerator />} /> {/* QR-Codes für Regale drucken */}
      <Route path="/vr/room-setup" element={<VRSetupMode />} /> {/* VR Setup (mode über URL-Parameter: ?mode=room oder ?mode=shelf) */}
      <Route path="/vr/room-scan" element={<VRSetupMode mode="room" />} /> {/* Legacy: VR-Scan */}
      <Route path="/vr/admin" element={<RoomAdmin />} /> {/* Admin-Panel zum Räume verwalten */}
      <Route path="/vr/shelf-setup" element={<VRSetupMode mode="shelf" />} /> {/* Legacy: Shelf Setup */}
      <Route path="/vr-setup" element={<VRSetupMode />} /> {/* Legacy: kombinierter Modus */}
      <Route path="/room-builder/:roomId" element={<RoomBuilder />} /> {/* Desktop 3D Room Builder */}
      
      {/* Alle anderen Routen mit normalem Layout */}
      <Route path="*" element={
        /* 🥽 Quest Auto-Redirect: Quest-User direkt zu /vr */
        isMetaQuest() && !window.location.pathname.startsWith('/vr') ? 
          <Navigate to="/vr" replace /> :
        <div className="App" style={{ background: darkMode ? '#1a1a1a' : '#fff', minHeight: '100vh' }}>
          <header style={{
            fontSize: 18,
            fontWeight: 'bold',
            textAlign: 'center',
            margin: '4px 0 8px 0',
            letterSpacing: 1,
            color: darkMode ? '#b0b0b0' : '#333'
          }}>
            PaketPilot
          </header>
          <div className="main-layout">
            <Routes>
              {/* Parameter mitnehmen (z. B. ?turbodemo=1 für die Turbo-Vorschau) */}
              <Route path="/" element={<Navigate to={{ pathname: '/pakete', search: window.location.search }} replace />} />
            <Route
              path="/pakete"
              element={
                <AddPackageWithAI
                  darkMode={darkMode}
                  setDarkMode={setDarkMode}
                  colors={colors}
                />
              }
            />
            <Route
              path="/tracking"
              element={
                <TrackingForm
                  darkMode={darkMode}
                  lang={lang}
                  colors={colors}
                  texts={texts}
                />
              }
            />
            <Route
              path="/statistics"
              element={
                <Statistics
                  refreshKey={location.key} // 🔄 Neu laden bei jedem Navigieren
                  pakete={pakete}
                  darkMode={darkMode}
                  lang={lang}
                  colors={colors}
                  texts={texts}
                />
              }
            />
            {/* Tablet: Auswahl Kunde / Paketverwaltung (Bench-Station) */}
            <Route
              path="/tablet-search"
              element={
                <TabletHome
                  pakete={pakete}
                  colors={colors}
                />
              }
            />
            <Route
              path="/tutorial"
              element={<Tutorial />}
            />
            <Route
              path="/chronik"
              element={<Chronik />}
            />
            <Route
              path="/dokumente"
              element={<DocumentManagement />}
            />
            <Route
              path="/admin"
              element={<AdminDashboard />}
            />
          </Routes>
        </div>
        {!(hideBottomNav || navHiddenByKeyboard || navHiddenByModal || navHiddenByStaff) && (
        <nav style={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          background: colors.box,
          borderTop: `1px solid ${colors.border}`,
          padding: '6px 4px 28px 4px',
          display: 'flex',
          justifyContent: 'space-around',
          alignItems: 'flex-start',
          zIndex: 1000,
          boxShadow: '0 -2px 10px rgba(0,0,0,0.1)'
        }}>
          <Link 
            to="/pakete" 
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              textDecoration: 'none',
              color: colors.text,
              fontSize: 10,
              padding: '4px 2px',
              minWidth: '45px',
              cursor: 'pointer'
            }}
          >
            <span style={{ fontSize: 20, marginBottom: 2 }}>📦</span>
            {t('packages.packages')}
          </Link>
          
          <Link 
            to="/tracking" 
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              textDecoration: 'none',
              color: colors.text,
              fontSize: 10,
              padding: '4px 2px',
              minWidth: '45px',
              cursor: 'pointer'
            }}
          >
            <span style={{ fontSize: 20, marginBottom: 2 }}>🔍</span>
            Tracking
          </Link>
          
          <Link 
            to="/tablet-search" 
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              textDecoration: 'none',
              color: colors.text,
              fontSize: 10,
              padding: '4px 2px',
              minWidth: '45px',
              cursor: 'pointer'
            }}
          >
            <span style={{ fontSize: 20, marginBottom: 2 }}>📱</span>
            Tablet
          </Link>
          
          <Link 
            to="/chronik" 
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              textDecoration: 'none',
              color: colors.text,
              fontSize: 10,
              padding: '4px 2px',
              minWidth: '45px',
              cursor: 'pointer'
            }}
          >
            <span style={{ fontSize: 20, marginBottom: 2 }}>📋</span>
            {t('chronik.chronik')}
          </Link>

          <Link 
            to="/dokumente" 
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              textDecoration: 'none',
              color: colors.text,
              fontSize: 10,
              padding: '4px 2px',
              minWidth: '45px',
              cursor: 'pointer'
            }}
          >
            <span style={{ fontSize: 20, marginBottom: 2 }}>📄</span>
            Docs
          </Link>
          
          <Link 
            to="/statistics" 
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              textDecoration: 'none',
              color: colors.text,
              fontSize: 10,
              padding: '4px 2px',
              minWidth: '45px',
              cursor: 'pointer'
            }}
          >
            <span style={{ fontSize: 20, marginBottom: 2 }}>📊</span>
            Stats
          </Link>
          
          {isAdmin && (
            <Link 
              to="/admin" 
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                textDecoration: 'none',
                color: colors.text,
                fontSize: 10,
                padding: '4px 2px',
                minWidth: '45px',
                cursor: 'pointer'
              }}
            >
              <span style={{ fontSize: 20, marginBottom: 2 }}>🛡️</span>
              Admin
            </Link>
          )}
        </nav>
        )}
        {!(hideBottomNav || navHiddenByKeyboard || navHiddenByModal || navHiddenByStaff) && <div style={{ height: '70px' }}></div>}
      </div>
      } />
    </Routes>
    </Suspense>
    </>
  );
}

export default App;

// 🔄 Update verfügbar: Dialog mit „Jetzt installieren" / „Später (5 Min.)"; während der Installation nur der Hinweis
function UpdateDialog({ update, onNow, onLater }) {
  const ctx = useContext(LanguageContext);
  const t = (k, de) => { try { const v = ctx && ctx.t ? ctx.t(k) : ''; return v && v !== k ? v : de; } catch { return de; } };
  const version = update.version ? `Version ${update.version}` : '';
  return (
    <div data-testid="update-dialog" data-installing={update.installing ? '1' : '0'} style={{ position: 'fixed', inset: 0, zIndex: 99999, background: 'rgba(0,0,0,0.75)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20, fontFamily: 'system-ui, sans-serif' }}>
      <div style={{ background: '#1a1d23', color: '#fff', borderRadius: 14, padding: 24, width: '100%', maxWidth: 400,
        textAlign: 'center', boxShadow: '0 10px 40px rgba(0,0,0,0.5)' }}>
        <div style={{ fontSize: 34, marginBottom: 8 }}>🔄</div>
        {update.installing ? (
          <>
            <div style={{ fontSize: 19, fontWeight: 800, marginBottom: 6 }}>{t('update.installing', 'Update wird installiert…')}</div>
            <div style={{ fontSize: 14, color: '#9aa0a8' }}>{version} {t('update.pleaseWait', 'Bitte warten, die App wird gleich neu geladen')}</div>
          </>
        ) : (
          <>
            <div style={{ fontSize: 19, fontWeight: 800, marginBottom: 6 }}>{t('update.updateAvailable', 'Update verfügbar')}{version ? ` · ${version}` : ''}</div>
            <div style={{ fontSize: 14, color: '#c9ced6', lineHeight: 1.5 }}>{t('update.newVersionHint', 'Eine neue Version der App ist da. Zum Installieren wird die App kurz neu geladen, laufende Scans vorher abschließen.')}</div>
            <div style={{ fontSize: 12, color: '#9aa0a8', marginTop: 8, lineHeight: 1.4 }}>{t('update.idleHint', 'Ohne Bedienung wird das Update nach 10 Minuten von selbst installiert.')}</div>
            <button type="button" data-testid="update-now" onClick={onNow}
              style={{ width: '100%', marginTop: 16, padding: '13px', borderRadius: 10, border: 'none', background: '#1976d2', color: '#fff', fontSize: 16, fontWeight: 700, cursor: 'pointer' }}>
              {t('update.installNow', 'Jetzt installieren')}
            </button>
            <button type="button" data-testid="update-later" onClick={onLater}
              style={{ width: '100%', marginTop: 8, padding: '12px', borderRadius: 10, border: '1px solid #555', background: 'transparent', color: '#fff', fontSize: 15, fontWeight: 600, cursor: 'pointer' }}>
              {t('update.later', 'Später (5 Min.)')}
            </button>
          </>
        )}
      </div>
    </div>
  );
}
